/**
 * Point d'entrée du bundle IIFE hébergé par le tracker.
 *
 * <script src="https://bugs.leptitlogiciel.fr/widget.js" data-api-key="pk_…" defer></script>
 *
 * Le script lit sa configuration dans les attributs data-* de son propre tag,
 * crée un conteneur en fin de <body> et monte l'UI dans un Shadow DOM ouvert :
 * aucune fuite CSS dans les deux sens, aucune dépendance côté hôte.
 */
import { createRoot } from "react-dom/client";
import { BugWidget } from "./ui/BugWidget";
import { injectStyles } from "./ui/styles";

const HOST_ID = "lpl-bugs-widget-host";

function mount(script: HTMLScriptElement): void {
  const apiKey = script.dataset.apiKey;
  if (!apiKey) {
    console.warn("[lpl-bugs-widget] attribut data-api-key manquant sur le tag <script> — widget non monté.");
    return;
  }

  // Origine du tracker = origine du script (le bundle est servi par le tracker).
  // data-tracker-url permet de la surcharger, p.ex. en dev quand le script
  // est servi par une autre app que le tracker.
  let trackerBase: string;
  try {
    trackerBase = new URL(script.dataset.trackerUrl || script.src, window.location.href).origin;
  } catch {
    trackerBase = window.location.origin;
  }

  if (document.getElementById(HOST_ID)) return; // double inclusion du script

  const host = document.createElement("div");
  host.id = HOST_ID;
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  injectStyles(shadow);

  const container = document.createElement("div");
  // L'Inspector repère le widget via ce marqueur ; on le pose aussi sur
  // l'hôte car elementFromPoint (côté document) renvoie l'hôte, pas le shadow.
  host.setAttribute("data-bug-widget", "");
  shadow.appendChild(container);

  createRoot(container).render(
    <BugWidget
      apiKey={apiKey}
      trackerBase={trackerBase}
      {...(script.dataset.commitSha ? { commitSha: script.dataset.commitSha } : {})}
    />,
  );
}

const current = document.currentScript;
if (current instanceof HTMLScriptElement) {
  if (document.body) {
    mount(current);
  } else {
    // Script dans <head> sans defer : on attend le DOM.
    document.addEventListener("DOMContentLoaded", () => mount(current));
  }
} else {
  console.warn("[lpl-bugs-widget] impossible de repérer le tag <script> (chargement en module ?) — widget non monté.");
}
